import { memo, useMemo } from 'react';
import { Skeleton } from '@mui/material';
import { ArgMdActionUsageDetails, ArgMetadatadInUseDetails, IArgumentMetadata } from '@tymely/atoms';
import { TreeNode, TreeView } from '@tymely/components';
import { routes } from '@tymely/config';
import { pluralize } from '@tymely/utils';
import { useNavigate } from 'react-router-dom';

export const ArgUsage = memo(
    (props: {
        argName: IArgumentMetadata['name'];
        loading?: boolean;
        error?: string;
        data?: ArgMetadatadInUseDetails;
    }) => {
        const navigate = useNavigate();
        const dependentArgNames = props.data?.dependent_arg_names ?? [];
        const intentsByOrgs = useMemo(
            () =>
                (props.data?.policies || []).reduce<Map<number, Map<number, ArgMdActionUsageDetails>>>((map, usage) => {
                    const intents = map.get(usage.organization_id) || new Map();
                    return map.set(usage.organization_id, intents.set(usage.policy_set_id, usage));
                }, new Map()),
            [props.data],
        );

        const templatesByOrgs = useMemo(
            () =>
                (props.data?.templates || []).reduce<Map<number, Map<number, ArgMdActionUsageDetails>>>(
                    (map, usage) => {
                        const actions = map.get(usage.organization_id) || new Map();
                        return map.set(usage.organization_id, actions.set(usage.action_id, usage));
                    },
                    new Map(),
                ),
            [props.data],
        );

        const actionsByOrgs = useMemo(
            () =>
                (props.data?.actions || []).reduce<Map<number, Map<number, ArgMdActionUsageDetails>>>((map, usage) => {
                    const actions = map.get(usage.organization_id) || new Map();
                    return map.set(usage.organization_id, actions.set(usage.action_id, usage));
                }, new Map()),
            [props.data],
        );

        const orgs = useMemo<Map<number, string>>(() => {
            const map = new Map<number, string>();
            if (!props.data) return map;

            (props.data.policies ?? [])
                .concat(props.data.templates ?? [])
                .concat(props.data.actions ?? [])
                .forEach(({ organization_id, organization_name }) => {
                    map.set(organization_id, organization_name);
                });
            return map;
        }, [intentsByOrgs, actionsByOrgs, templatesByOrgs]);

        const navigateToPolicy = (usage: ArgMdActionUsageDetails, type: 'policy' | 'action') => {
            if (type === 'policy') {
                window.open(
                    `${window.location.origin}${routes.graphPolicyEditor}/${usage.organization_id}/${usage.policy_set_id}`,
                );
            }
            window.open(
                `${window.location.origin}${routes.graphPolicyEditor}/${usage.organization_id}/${usage.policy_set_id}/${usage.workflow_id}?actionId=${usage.action_id}`,
            );
        };

        const navigateToArgEdit = (argName: IArgumentMetadata['name']) => {
            navigate(`${routes.argumentMetadataView}?edit=${argName}`);
        };

        const tree: TreeNode[] = useMemo(
            () =>
                Array.from(orgs.entries())
                    .map(([orgId, orgName]) => {
                        const intentChildren = Array.from(intentsByOrgs.get(orgId) || [])
                            .map<TreeNode | null>(([intentId, usage]) => {
                                return {
                                    id: String(intentId),
                                    name: `[${intentId}]: ${usage.intent_name}`,
                                    onClick: () => {
                                        navigateToPolicy(usage, 'policy');
                                    },
                                };
                            })
                            .filter(Boolean);

                        const templateChildren = Array.from(templatesByOrgs.get(orgId) || [])
                            .map<TreeNode | null>(([actionId, usage]) => {
                                return {
                                    id: String(actionId),
                                    name: `[workflow ${usage.workflow_id}] [intent ${usage.policy_set_id}] ${usage.workflow_name}`,
                                    onClick: () => {
                                        navigateToPolicy(usage, 'action');
                                    },
                                };
                            })
                            .filter(Boolean);

                        const actionChildren = Array.from(actionsByOrgs.get(orgId) || [])
                            .map<TreeNode | null>(([actionId, usage]) => {
                                return {
                                    id: String(actionId),
                                    name: `[${usage.action_id}]: ${usage.workflow_name} - ${usage.action_name}`,
                                    onClick: () => {
                                        navigateToPolicy(usage, 'action');
                                    },
                                };
                            })
                            .filter(Boolean);

                        return {
                            id: String(orgId),
                            name: orgName,
                            children: [
                                intentChildren.length
                                    ? {
                                          id: `intents${orgId}`,
                                          name: 'Intents',
                                          children: intentChildren,
                                      }
                                    : null,
                                templateChildren.length
                                    ? {
                                          id: `templates${orgId}`,
                                          name: 'Templates',
                                          children: templateChildren,
                                      }
                                    : null,
                                actionChildren.length
                                    ? {
                                          id: `action${orgId}`,
                                          name: 'Actions',
                                          children: actionChildren,
                                      }
                                    : null,
                            ].filter(Boolean),
                        };
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .concat(
                        ...[
                            dependentArgNames.length > 0
                                ? {
                                      id: 'Arguments',
                                      name: 'Arguments',
                                      children: dependentArgNames.map((argName) => ({
                                          id: `arg-${argName}`,
                                          name: argName,
                                          children: [],
                                          onClick: () => navigateToArgEdit(argName),
                                      })),
                                  }
                                : null,
                        ].filter(Boolean),
                    ),
            [intentsByOrgs, templatesByOrgs, actionsByOrgs, dependentArgNames],
        );

        const label = useMemo(() => {
            const orgUsageStr = orgs.size > 0 ? `${orgs.size} ${pluralize('org', orgs.size)}` : null;
            const argUsageStr =
                dependentArgNames.length > 0
                    ? `${dependentArgNames.length} ${pluralize('arg', dependentArgNames.length)}`
                    : 0;
            const usedByStr = [orgUsageStr, argUsageStr].filter(Boolean).join(', ');
            return usedByStr ? `Used by ${usedByStr}` : 'Unused';
        }, [orgs, dependentArgNames]);

        if (props.loading && !props.error)
            return (
                <Skeleton variant="text" animation="wave" height="2em" width="10em" sx={{ ml: 'auto', mr: 'auto' }} />
            );

        return <TreeView tree={props.error ? [] : tree} label={props.error ?? label} />;
    },
);

ArgUsage.displayName = 'ArgUsage';
